import { cx } from '@emotion/css';
import { Divider } from '@sortlist-frontend/design-system/legacy';
import { useTrack } from '@sortlist-frontend/tracking';
import { isBrowser, isNonEmptyString } from '@sortlist-frontend/utils';
import dynamic from 'next/dynamic';
import { ForwardedRef, MutableRefObject, useEffect, useRef, useState } from 'react';

import { usePublicAppContext } from '_core/context/public-app-context';
import { useComparedAgencies } from '_core/hooks/use-compared-agencies';
import { missingAgencyLogo } from '_core/media/get-media.utils';
import { BottomBar } from '_features/AgencyProfile/components/BottomBar';
import type { PortfolioPanelProps } from '_features/AgencyProfile/components/PortfolioPanel/PortfolioPanel';
import { SectionsDetails } from '_features/AgencyProfile/components/SectionsDetails';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';
import * as S from '_features/AgencyProfile/styles';

import { AboutPanel } from './components/AboutPanel/AboutPanel';
import { AwardsPanel } from './components/AwardsPanel/AwardsPanel';
import { ContactPanel } from './components/ContactPanel/ContactPanel';
import { HeroPanel } from './components/HeroPanel/HeroPanel';
import { ReviewsPanel } from './components/ReviewsPanel/ReviewsPanel';
import { ServicesPanel } from './components/ServicesPanel/ServicesPanel';
import { TeamPanel } from './components/TeamPanel/TeamPanel';
import { getActiveSections, getAgencyStats, type AgencyStats, type Section } from './utils';

const PortfolioPanel = dynamic<PortfolioPanelProps>(
  () => import('_features/AgencyProfile/components/PortfolioPanel/PortfolioPanel').then((mod) => mod.PortfolioPanel),
  { ssr: false },
);

const SimilarAgenciesSection = dynamic(
  () => import('./components/SimilarAgenciesPanel/SimilarAgenciesSection').then((mod) => mod.SimilarAgenciesSection),
  { ssr: false },
);

type Props = {
  children?: never;
  scrollableCtn?: ForwardedRef<HTMLDivElement>;
  top: string[] | string | null;
};

export const AgencyProfilePage = (props: Props) => {
  const { scrollableCtn } = props;
  const { locale: localeFromContext, pageData } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const locale = localeFromContext as string;
  const { track } = useTrack({ app: 'public', locale });
  const [heroSections, setHeroSections] = useState<Section[]>([]);
  const [isStickyBar, setStickyBar] = useState(false);
  const bottomBarLimit = useRef<HTMLDivElement>(null);

  const { data: { agency: agencyProfile } = {} } = useGetAgencyProfile({ agencySlug, locale });

  const { description, tagline } = agencyProfile ?? {};
  const worksCount = agencyProfile?.works.totalCount ?? 0;
  const awardsCount = agencyProfile?.awards.totalCount ?? 0;

  const bottomBarLimitCurrent = (bottomBarLimit as MutableRefObject<HTMLDivElement>)?.current;
  const scrollableCtnCurrent = (scrollableCtn as MutableRefObject<HTMLDivElement>)?.current;
  const agencyStats: AgencyStats = getAgencyStats(agencyProfile);
  const { name: agencyName, slug, uuid, logoUrl, paidMember } = agencyProfile ?? {};
  const agencyLogoUrl = logoUrl ?? missingAgencyLogo;
  const showTeamSection = agencyStats.teamMembersCover || agencyStats.teamStory;
  const servicesCount = agencyProfile?.services?.totalCount ?? 0;
  const servicePanelClassName = servicesCount > 0 ? '' : 'hide';
  const portfolioPanelClassName = agencyStats.worksCount > 0 ? '' : 'hide';
  const teamPanelClassName = showTeamSection ? '' : 'hide';
  const awardsPanelClassName = agencyStats.awardsCount > 0 ? '' : 'hide';

  const { comparedAgencies, setComparedAgencies } = useComparedAgencies();

  useEffect(() => {
    // 'agencyImpression' events REQUIRE the parameters: agencyName,
    // agencyPlan (which should be visibility_plan), agencySlug, app, type.
    if (agencyProfile) {
      track({
        slug: 'agencyImpression',
        data: {
          agencyName: agencyName ?? '',
          agencyPlan: paidMember ? 'paid' : 'free',
          agencySlug: slug ?? '',
          agencyUuid: uuid ?? '',
          type: 'agencyProfile',
          pageType: 'agency-profile',
        },
      });
    }
  }, [agencyProfile]);

  useEffect(() => {
    setHeroSections(getActiveSections(SectionsDetails, agencyStats, agencyProfile));
  }, [agencyProfile]);

  const handleActivePanel = (section?: Section | undefined) => {
    if (isBrowser()) {
      const { id } = section ?? {};
      location.hash = '';
      if (isNonEmptyString(id)) {
        location.hash = `#${id}`;
      } else {
        window.scrollTo(0, 0);
      }
    }
  };

  const offices = agencyProfile?.offices?.nodes?.filter((n): n is NonNullable<typeof n> => n != null) ?? [];

  const showContact = () => {
    const isOffice = offices.length > 0;
    const isFree = agencyProfile?.paidMember === false;

    if (isFree && isOffice) return true;

    return !isFree && (isOffice || agencyProfile?.website != null || agencyProfile?.socialProfiles != null);
  };

  const handleStickyBottomBar = () => {
    const bottomLimit = bottomBarLimitCurrent?.getBoundingClientRect().top;
    const currentBottomPosition = scrollableCtnCurrent?.getBoundingClientRect().bottom;
    const overBottomLimit = currentBottomPosition > bottomLimit;
    const sticky = !overBottomLimit && scrollableCtnCurrent?.scrollTop > 900;
    setStickyBar(sticky);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleStickyBottomBar, true);
    return () => window.removeEventListener('scroll', handleStickyBottomBar, true);
  }, [bottomBarLimitCurrent, scrollableCtnCurrent]);

  if (agencyProfile == null) return null;

  const showSimilarPanel = !agencyProfile?.paidMember;

  return (
    <S.View className="layout-xs-fill layout-xs-column">
      {isStickyBar && <BottomBar />}

      <div className="container-lg layout-column max-sm:px-0 sm:pt-8 lg:pb-16 flex-xs">
        <S.Shape className="bg-neutral-50 shadow-2 rounded-xl flex-1 layout-xs-column">
          <HeroPanel sections={heroSections.filter((section) => section.id !== 'similar')} />
          <AboutPanel
            handleActivePanel={handleActivePanel}
            agencyStats={agencyStats}
            comparedAgencies={comparedAgencies}
            setComparedAgencies={setComparedAgencies}
            tagline={tagline}
            description={description}
          />

          <Divider className={cx({ hide: !showSimilarPanel }, 'similar')} />

          {showSimilarPanel && <SimilarAgenciesSection agencyName={agencyName ?? ''} agencySlug={agencySlug} />}

          <Divider className={cx(servicePanelClassName)} />

          {servicesCount > 0 && (
            <ServicesPanel className={cx(servicePanelClassName)} handleActivePanel={handleActivePanel} />
          )}

          <Divider className={cx(portfolioPanelClassName)} />

          {worksCount > 0 && <PortfolioPanel className={cx(portfolioPanelClassName)} agencyLogoUrl={agencyLogoUrl} />}
          <Divider className={cx(teamPanelClassName)} />

          <TeamPanel className={cx(teamPanelClassName)} />

          <Divider className={cx(awardsPanelClassName)} />

          {awardsCount > 0 && <AwardsPanel agencyName={agencyName} className={cx(awardsPanelClassName, 'flex-xs')} />}

          <Divider />

          <ReviewsPanel />

          <div
            className={cx({ hide: agencyProfile?.website == null && agencyProfile?.mainOffice?.address == null })}
            ref={bottomBarLimit}>
            <Divider className={cx({ hide: agencyProfile?.paidMember !== true })} />
            {showContact() && <ContactPanel />}
          </div>
        </S.Shape>
      </div>
    </S.View>
  );
};
